import { Erro } from "@/repositorios/erros"
import { ParamsGeral, ResultadoApiGET, CabecalhoBase } from "@/types/api"
import { reactive } from "vue"
import { usarUrl } from "@/composable/UsarUrl/usarUrl"
import i18n from "@/i18n"

export interface LeitorErro {
  status: "erro";
  mensagem: string;
  mensagemParaQuem: "usuario" | "dev";
}
export interface LeitorPendente {
  status: "pendente";
}
export interface LeitorSucesso<T = any> {
  status: "sucesso";
  dados: T[]
}
export interface LeitorVazio {
  status: "vazio"
}
export interface LeitorNegado {
  status: "negado"
}
export interface LeitorCancelado {
  status: "cancelado"
}
export type LeitorEstados<T = any> = (
  | LeitorErro
  | LeitorPendente
  | LeitorSucesso<T>
  | LeitorVazio
  | LeitorNegado
  | LeitorCancelado
)

type Repositorio<R, P = any, C = any> = (params: P, cabecalho: C) => Promise<ResultadoApiGET<R>>;
type FuncaoResultante<R> = (resultado: R) => void;
type Resolver<R> = { erro?: FuncaoResultante<LeitorErro>, sucesso: FuncaoResultante<R> };

const { t } = i18n.global

export interface Paginador<R = any, P = any, C = any> {
  resultado: LeitorEstados<R>;
  resultadosNestaPagina: number;
  consultaPaginaAnterior: string;
  consultaProximaPagina: string;
  consultaPrimeiraPagina: string;
  consultaUltimaPagina: string;
  totalDeResultados: number;
  paginaAtual: number;
  primeiraPagina: number;
  itensPorPagina: number;
  repositorio: Repositorio<R, P, C>;
  cabecalhoAdicionados: any;
  parametrosBase: any;
  parametrosIniciais: any;
  parametrosTemporarios: any;
  permissaoParaExecutar: boolean;
  readonly porPagina: (porPagina: number) => Paginador<R, P>;
  readonly pagina: (pagina: number) => Paginador<R, P>;
  readonly anterior: () => Promise<void>;
  readonly proximo: () => Promise<void>;
  readonly proximoComAcumulo: () => Promise<void>;
  readonly definirPagina: () => number;
  readonly garantirPaginaAtual: () => void;
  readonly atualizarParametro: (nomeParam: string, operador: string, valor: string) => void;
  readonly sobrescreverParametros: (novoParams: any) => void;
  readonly sobrescreverCabecalhos: (novoCabecalho: any) => void;
  readonly incrementarParametros: (novoParams: any) => void;
  readonly resetarParametrosParaEstadoInicial: () => void;
  readonly removerParametros: (parametros: string[]) => void;
  readonly mudarPaginaNosParametros: (novaPagina: string) => void;
  readonly mudarPorPaginaNosParametros: (novoPorPagina: string) => void;
  readonly mudarEstado: (novoResultado: LeitorEstados<R>) => void;
  readonly executar: () => Promise<void>;
  readonly executarComAcumulo: () => Promise<void>;
  readonly processarResultados: (resultados: R[]) => void;
  readonly processarResultadosComAcumulo: (resultadosAntigos: R[], resultadosNovos: R[]) => void;
  readonly mudarEstadoParaPendente: () => void;
  readonly mudarEstadoParaVazio: () => void;
  readonly mudarEstadoParaNegado: () => void;
  readonly mudarEstadoParaCancelado: () => void;
  readonly mudarEstadoParaErro: (mensagem: string, mensagemParaQuem: "usuario" | "dev") => void;
  readonly mudarEstadoParaSucesso: (dados: R[]) => void;
  readonly obterParametros: () => any;
  readonly fazerRequisicaoUsandoRepositorio: (parametros: P, cabecalhos: C) => Promise<R[]>;
  readonly fazerRequisicaoComParametros: () => Promise<R[]>;
  readonly tratarErroDeExecutor: (erro: any) => void;
  readonly possuiMaisItensNaProximaPagina: () => boolean;
}

const inicializarItensPorPagina = (parametros) => {
  const valorPadrao = 10
  if (!parametros) { return valorPadrao }
  const porPagina = parametros["por-pagina"]
  return porPagina ? parseInt(porPagina) : valorPadrao
}

/**
 * @deprecated utilizar classes UsarLeitorDeDadosAcumulados ou UsarLeitorDeDadosPaginados
 */
export function usarLeitorDeDados() {

  const { converterQueryStringParaObjeto, pegarQueryStringDaRota } = usarUrl()

  const leitor = <R, P = ParamsGeral, C = CabecalhoBase>(repositorio: Repositorio<R, P>, params?: P, cabecalho?: C) =>
    reactive<Paginador<R, P>>({
      resultado: {
        status: "pendente"
      },
      resultadosNestaPagina: 0,
      consultaPaginaAnterior: "",
      consultaProximaPagina: "",
      consultaPrimeiraPagina: "",
      consultaUltimaPagina: "",
      totalDeResultados: 0,
      paginaAtual: 0,
      primeiraPagina: 0,
      itensPorPagina: inicializarItensPorPagina(params),
      repositorio: repositorio,
      parametrosBase: params || {},
      parametrosIniciais: params || {},
      parametrosTemporarios: {},
      cabecalhoAdicionados: cabecalho || {},
      permissaoParaExecutar: true,
      pagina(pagina: number) {
        this.paginaAtual = pagina
        this.mudarPaginaNosParametros(pagina.toString())
        return this
      },
      porPagina(porPagina: number) {
        this.itensPorPagina = porPagina
        this.mudarPorPaginaNosParametros(porPagina.toString())
        return this
      },
      anterior() {
        const parametrosComoObjeto = converterQueryStringParaObjeto(this.consultaPaginaAnterior)
        this.sobrescreverParametros(parametrosComoObjeto)
        return this.executar()
      },
      async proximo() {
        const parametrosComoObjeto = converterQueryStringParaObjeto(this.consultaProximaPagina)
        this.sobrescreverParametros(parametrosComoObjeto)
        return this.executar()
      },
      proximoComAcumulo() {
        const parametrosComoObjeto = converterQueryStringParaObjeto(this.consultaProximaPagina)
        this.sobrescreverParametros(parametrosComoObjeto)
        return this.executarComAcumulo()
      },
      definirPagina() {
        return this.paginaAtual * this.itensPorPagina
      },
      garantirPaginaAtual() {
        const paginaAtual = this.resultadosNestaPagina == 1 && this.paginaAtual > 0 ?
          --this.paginaAtual : this.paginaAtual

        this.pagina(paginaAtual)
      },
      sobrescreverParametros(novoParams: P) {
        this.parametrosBase = { ...novoParams }
      },
      sobrescreverCabecalhos(novoCabecalho: C) {
        this.cabecalhoAdicionados = { ...novoCabecalho }
      },
      incrementarParametros(novoParams: P) {
        this.resetarParametrosParaEstadoInicial()
        this.parametrosTemporarios = novoParams
      },
      resetarParametrosParaEstadoInicial() {
        this.parametrosBase = this.parametrosIniciais
        this.parametrosTemporarios = {}
      },
      removerParametros(parametros: string[]): void {
        parametros.forEach(parametro => {
          delete this.parametrosBase[parametro]
        })
      },
      mudarPaginaNosParametros(novaPagina: string) {
        this.parametrosBase.pagina = novaPagina
      },
      mudarPorPaginaNosParametros(novoPorPagina: string) {
        this.parametrosBase["por-pagina"] = novoPorPagina
      },
      mudarEstado(novoResultado: LeitorEstados<R>) {
        this.resultado = novoResultado
      },
      atualizarParametro(nomeParam: string, operador: string, valor: string) {
        this.parametrosBase[nomeParam] = `${operador}.${valor}`
      },
      mudarEstadoParaPendente() {
        this.mudarEstado({
          status: "pendente"
        })
      },
      mudarEstadoParaVazio() {
        this.mudarEstado({
          status: "vazio"
        })
      },
      mudarEstadoParaNegado() {
        this.mudarEstado({
          status: "negado"
        })
      },
      mudarEstadoParaCancelado() {
        this.mudarEstado({
          status: "cancelado"
        })
      },
      mudarEstadoParaErro(mensagem: string, mensagemParaQuem: "usuario" | "dev") {
        this.mudarEstado({
          status: "erro",
          mensagem,
          mensagemParaQuem
        })
      },
      mudarEstadoParaSucesso(dados: R[]) {
        this.mudarEstado({
          status: "sucesso",
          dados
        })
      },
      obterParametros(): any {
        return {
          ...this.parametrosBase,
          ...this.parametrosTemporarios
        }
      },
      async fazerRequisicaoUsandoRepositorio(parametros: P, cabecalhos: C) {
        const {
          resultados,
          resultadosNessaPagina,
          rotaPaginaAnterior,
          rotaProximaPagina,
          rotaPrimeiraPagina,
          rotaUltimaPagina,
          totalDeResultados
        } = await this.repositorio(parametros, cabecalhos)

        this.resultadosNestaPagina = resultadosNessaPagina
        this.consultaPaginaAnterior = pegarQueryStringDaRota(rotaPaginaAnterior)
        this.consultaProximaPagina = pegarQueryStringDaRota(rotaProximaPagina)
        this.consultaPrimeiraPagina = pegarQueryStringDaRota(rotaPrimeiraPagina)
        this.consultaUltimaPagina = pegarQueryStringDaRota(rotaUltimaPagina)
        this.totalDeResultados = totalDeResultados

        return resultados
      },
      tratarErroDeExecutor(erro: any) {
        const erroCapturado = erro as Erro
        if (erroCapturado.codigo === 403) {
          this.mudarEstadoParaNegado()
        } else if (erroCapturado.codigo === "cancelado") {
          this.mudarEstadoParaCancelado()
        } else {
          this.mudarEstadoParaErro(erroCapturado.mensagem, "usuario")
        }
      },
      fazerRequisicaoComParametros() {
        const parametros = this.obterParametros()
        const cabecalhos = this.cabecalhoAdicionados ? this.cabecalhoAdicionados : {} as C
        return this.fazerRequisicaoUsandoRepositorio(parametros, cabecalhos)
      },
      async executar() {
        try {
          this.mudarEstadoParaPendente()
          const resultados = await this.fazerRequisicaoComParametros()
          this.processarResultados(resultados)
        } catch (erro) {
          this.tratarErroDeExecutor(erro)
        }
      },
      async executarComAcumulo() {
        try {
          const ultimoStatusFoiDeSucesso = this.resultado.status === "sucesso"
          const resultadosAntigos = ultimoStatusFoiDeSucesso ? (this.resultado as LeitorSucesso<R>).dados : []
          this.mudarEstadoParaPendente()
          const resultadosNovos = await this.fazerRequisicaoComParametros()
          this.processarResultadosComAcumulo(resultadosAntigos, resultadosNovos)
        } catch (erro) {
          this.tratarErroDeExecutor(erro)
        }
      },
      processarResultados(resultados) {
        const possuiDados = resultados.length > 0
        if (possuiDados) {
          this.mudarEstadoParaSucesso(resultados)
        } else {
          this.mudarEstadoParaVazio()
        }
      },
      processarResultadosComAcumulo(resultadosAntigos, resultadosNovos) {
        const resultadosAcumulados = [...resultadosAntigos, ...resultadosNovos]
        const possuiDados = resultadosAcumulados.length > 0
        if (possuiDados) {
          this.mudarEstadoParaSucesso(resultadosAcumulados)
        } else {
          this.mudarEstadoParaVazio()
        }
      },
      possuiMaisItensNaProximaPagina() {
        const parametros = this.obterParametros()

        if ('por-pagina' in parametros === false) {
          throw new Error(t("parametroNaoEncontrado"))
        }

        const porPagina = parseInt(parametros['por-pagina'])

        const aindaExistemMaisResultados = this.totalDeResultados > this.resultadosNestaPagina
        const atingiuLimiteDePagina = this.resultadosNestaPagina >= porPagina
        return aindaExistemMaisResultados && atingiuLimiteDePagina
      }
    })

  const resolverLeitor = <R>(resultado: LeitorEstados<R>, { erro, sucesso }: Resolver<R[]>) => {
    if (resultado.status === "sucesso") { sucesso(resultado.dados) }
    if (resultado.status === "erro" && erro) { erro(resultado) }
  }

  return {
    leitor,
    resolverLeitor
  }
}
